// File: typescript/apps/front-end/src/main/react/thoughts/ThoughtChainDisplayer.tsx
//
// Linear comment-chain ("Read as conversation") view. Where ThoughtReplyTree renders the whole subtree
// as an indented tree, this renders ONE lineage — ancestors → anchor → top-ranked child per level —
// as a continuous, chat-style column, so a reader can follow a single exchange end to end.
//
// Data:
//   - GET /ajax/thoughts/ancestors (up): root → … → anchor's parent (ClientFactory.listThoughtAncestors)
//   - GET /ajax/thoughts/chain     (down): anchor (depth 0) → top child per level → leaf (listThoughtChain)
// At any node with more than one child, the spine follows the top-ranked child and a fork switch offers
// the siblings (paginated GET /ajax/thoughts/replies). Picking a sibling re-linearizes the spine below
// that fork. If the branch is truncated by the fetch limit, the tail offers "Continue this branch".
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, ChevronDown, CornerDownRight, GitBranch } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { scrollElementIntoViewWithOffset } from "@/main/common/ScrollUtils";
import ThoughtDisplayerInDiscussion from "@/main/react/thoughts/ThoughtDisplayerInDiscussion";
import ThoughtTextEditor from "@/main/react/editor/thought/ThoughtTextEditorLazy";
import { showThoughtTextEditorDialog } from "@/main/react/editor/thought/ThoughtTextEditorDialog";
import type { DialogController } from "@/main/react/Dialog/Dialog";
import type { ReplyTreeCommonProps, ThreadNode } from "@/main/react/thoughts/ThoughtThread";
import { getDisplayUsername, hasDisplayUsername } from "@/main/common/UsernameDisplayUtils";
import { ClientFactory } from "@/main/common/ClientFactory";
import { MetaHandler } from "@/main/common/MetaHandler";
import { useIsMobile } from "@/hooks/use-mobile";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import { PaginatedListRetryState } from "@/main/react/list/PaginatedListState";
import logger from "@/lib/logger";

// A lineage can be arbitrarily deep (the depth-8 creation cap was removed 2026-07-25), and the server
// clamps one chain read to MAX_READ_DEPTH_LEVELS_PER_REQUEST = 8 levels regardless of what we ask for.
// So this request returns at most 8 and "Continue this branch" is a genuinely reachable path, not the
// dead branch it used to be — do not assume a lineage arrives whole.
const CHAIN_FETCH_LIMIT = 20;
const FORK_PICKER_PAGE_SIZE = 20;

function toNodeArray(data: unknown): ThreadNode[] {
    return Array.isArray(data) ? (data as ThreadNode[]) : [];
}

export default function ThoughtChainDisplayer(props: {
    common: ReplyTreeCommonProps;
    anchorArgumentPublicId: string;
    /**
     * Hide the in-body "Back to post" link. Set by hosts that are not the discussion page and therefore
     * own their own way out — the notification conversation dialog puts "Open the full post" in its
     * footer, where a docked action belongs, and "Back to post" would be a lie in a surface the member
     * never navigated to the post from.
     */
    hideBackToDiscussion?: boolean;
    /**
     * Suppress the per-node reply affordance, leaving the host to own a single composer.
     *
     * <p>Load-bearing inside a dialog: on mobile the per-node path calls `showThoughtTextEditorDialog`,
     * which would stack a SECOND modal over the first. It would not crash, but it would be a worse
     * surface than the docked composer the host already has, and one Escape would dismiss both — nothing
     * arbitrates between the two, since neither `showDialog` nor the shared `DialogContent` consults a
     * modal stack. (The one that looked like it did, `hasOpenTopLayerModal()`, was never written to and
     * was deleted on 2026-09-04.) It is also a comprehension problem: a chain with six reply targets and
     * no visible tree gives the member no way to tell what they are answering.
     */
    suppressNodeComposers?: boolean;
    /**
     * Bump to force a chain reload from a host that published outside this component — the dialog's
     * docked composer. A plain counter rather than a store: nothing subscribes to it, it is read once
     * per change in one effect, and it never re-enters the render path (rule 31's concern is a snapshot
     * whose identity changes for a write that changed nothing; this only changes when something did).
     */
    reloadSignal?: number;
}) {
    const { common, anchorArgumentPublicId, hideBackToDiscussion = false, suppressNodeComposers = false,
        reloadSignal = 0 } = props;
    const { localizer, discussionSlug } = common;

    const [ancestors, setAncestors] = useState<ThreadNode[] | null>(null);
    // U38 (`DISCUSSION-106`): a press on the chain-of-one invitation asks the ANCHOR row to open its own
    // reply (the phone's sheet, the desktop's inline composer). A nonce, so each press is one request.
    const [anchorReplyRequest, setAnchorReplyRequest] = useState(0);
    const [spine, setSpine] = useState<ThreadNode[] | null>(null);
    // F-F2 (2026-08-27): failure is its own state, never inferred from the list (rule 23's state
    // contract). The old catches wrote `[]`, so `?view=chain` told a reader a thought had no thread
    // whenever the fetch failed. Two flags because the two fetches fail independently; either one
    // renders the failure panel, and retry (a nonce bump) re-runs both.
    const [ancestorsLoadFailed, setAncestorsLoadFailed] = useState(false);
    const [chainLoadFailed, setChainLoadFailed] = useState(false);
    const [retryNonce, setRetryNonce] = useState(0);

    const reloadChain = useCallback(async () => {
        try {
            const resp = await ClientFactory.listThoughtChain<ThreadNode[]>(anchorArgumentPublicId, "best", CHAIN_FETCH_LIMIT);
            setSpine(toNodeArray(resp.data));
            setChainLoadFailed(false);
        } catch (err) {
            logger.error("[ThoughtChainDisplayer] failed to load chain", err);
            setChainLoadFailed(true);
        }
    }, [anchorArgumentPublicId]);

    useEffect(() => {
        let cancelled = false;
        setAncestors(null);
        setSpine(null);
        setAncestorsLoadFailed(false);
        setChainLoadFailed(false);
        ClientFactory.listThoughtAncestors<ThreadNode[]>(anchorArgumentPublicId)
            .then((resp) => { if (!cancelled) setAncestors(toNodeArray(resp.data)); })
            .catch((err) => {
                logger.error("[ThoughtChainDisplayer] failed to load ancestors", err);
                if (!cancelled) setAncestorsLoadFailed(true);
            });
        void reloadChain();
        return () => { cancelled = true; };
    }, [anchorArgumentPublicId, reloadChain, retryNonce]);

    /**
     * DISCUSSION-49 — the chain route honours the same arrival contract the plain permalink already
     * does: the anchor is what the URL asked for.
     *
     * <p>Every route into this view hands a member ONE thought (a share, a notification, a permalink).
     * Without this, arriving cold at `?view=chain` put the anchor 952 CSS px down an 839 CSS px
     * viewport and nothing scrolled, so the first screenful was entirely the post the member did not
     * click. The page's own cold-arrival scroll is gated on `DEEP_LINKED_DISCUSSION_SECTIONS`, which
     * does not contain `"opening"` — the section a thought permalink resolves to — and the no-parameter
     * control only works because it renders `ThoughtInDiscussionCardDisplayer` in
     * `OPENING_HIGHLIGHTED`, whose own mount effect scrolls. The chain branch returns before that card
     * is ever reached, so the scroll has to live here.
     *
     * <p><b>Three guards, all load-bearing.</b> Once per anchor, via the ref — a re-render, a branch
     * switch or a published reply must not yank the viewport back. Only from the TOP of an un-anchored
     * document: a restored scroll position on Back and a `#thought-…` hash are somebody else's answer
     * to the same question, and overriding either would be this row's defect in the other direction
     * (the identical predicate `DiscussionPage`'s effect uses). And not when a host owns its own
     * framing — `hideBackToDiscussion` is set exactly by the notification dialog, where the member
     * never navigated to a page and there is no page scroll that would mean anything.
     *
     * <p>Nothing here writes history (rule 33); the effect only moves the viewport.
     */
    const chainViewRef = useRef<HTMLDivElement | null>(null);
    const arrivalScrollDoneForAnchorRef = useRef<string | null>(null);
    useEffect(() => {
        if (hideBackToDiscussion || spine === null || ancestors === null) {
            return;
        }
        if (arrivalScrollDoneForAnchorRef.current === anchorArgumentPublicId) {
            return;
        }
        arrivalScrollDoneForAnchorRef.current = anchorArgumentPublicId;
        if (typeof window === "undefined" || window.scrollY > 0 || window.location.hash) {
            return;
        }
        const anchorEl = chainViewRef.current?.querySelector<HTMLElement>('[data-thought-chain-anchor="true"]');
        if (!anchorEl) {
            return;
        }
        scrollElementIntoViewWithOffset(anchorEl, { attempts: 1 }).catch(() => {
            /* a scroll position is never worth taking the island down for */
        });
    }, [hideBackToDiscussion, spine, ancestors, anchorArgumentPublicId]);

    // A host-driven reload (the dialog's docked composer published). Skipped on the first render — the
    // effect above already loaded the chain, and reloading it again here would double every open.
    const lastHandledReloadSignal = useRef(reloadSignal);
    useEffect(() => {
        if (lastHandledReloadSignal.current === reloadSignal) {
            return;
        }
        lastHandledReloadSignal.current = reloadSignal;
        void reloadChain();
    }, [reloadSignal, reloadChain]);

    // Switch which sibling branch the spine follows below fork node `index`: keep the spine up to and
    // including the fork node, then append the chosen child's own chain (which starts with that child).
    const handleSwitchBranch = useCallback(async (index: number, chosenChildPublicId: string) => {
        try {
            const resp = await ClientFactory.listThoughtChain<ThreadNode[]>(chosenChildPublicId, "best", CHAIN_FETCH_LIMIT);
            const chosen = toNodeArray(resp.data);
            setSpine((prev) => (prev ? [...prev.slice(0, index + 1), ...chosen] : chosen));
        } catch (err) {
            logger.error("[ThoughtChainDisplayer] failed to switch branch", err);
        }
    }, []);

    // Extend a truncated branch: the tail's own chain starts with the tail, so append everything after it.
    const handleContinueBranch = useCallback(async (index: number, tailPublicId: string) => {
        try {
            const resp = await ClientFactory.listThoughtChain<ThreadNode[]>(tailPublicId, "best", CHAIN_FETCH_LIMIT);
            const more = toNodeArray(resp.data).slice(1);
            setSpine((prev) => (prev ? [...prev.slice(0, index + 1), ...more] : more));
        } catch (err) {
            logger.error("[ThoughtChainDisplayer] failed to continue branch", err);
        }
    }, []);

    const backToDiscussion = hideBackToDiscussion ? null : (
        <a
            href={`/discussion/${encodeURIComponent(discussionSlug)}/conversations`}
            // DISCUSSION-66 — "Back to post" is one of the four bare inline anchors carrying all thread
            // navigation on this surface. Its hit box was its line box (~20 CSS px at text-sm) with no
            // padding and no floor, while every button beside it inherits 44 px automatically. The
            // sanctioned mechanism here is the height floor, applied on a coarse pointer only so mouse
            // density is untouched; it must be `min-h-11` and not `touch-hit-44`, because an overlay
            // pseudo-element would collide with the stacked sibling below (03-tap-targets.css:70).
            className="mb-3 inline-flex items-center gap-1 px-3 text-sm font-medium text-primary no-underline hover:underline touch-manipulation [@media(pointer:coarse)]:min-h-11"
            data-playwright-id="thought-chain-back-to-discussion"
        >
            <ArrowLeft className="size-4" aria-hidden="true" />
            {localizer("thread.back_to_discussion")}
        </a>
    );

    // Checked before the loading branch: a failed fetch leaves its list null, and without this the
    // view would show the loading line forever. The back link stays so the view is never a dead end.
    if (ancestorsLoadFailed || chainLoadFailed) {
        return (
            <div className="w-full min-w-0 max-w-full" data-playwright-id="thought-chain-view">
                {backToDiscussion}
                <PaginatedListRetryState
                    message={localizer("shared.action.failed")}
                    retryLabel={localizer("literal.retry")}
                    onRetry={async () => setRetryNonce((nonce) => nonce + 1)}
                />
            </div>
        );
    }

    if (ancestors === null || spine === null) {
        return (
            <div className="w-full min-w-0 max-w-full" data-playwright-id="thought-chain-view">
                {backToDiscussion}
                {/* DISCUSSION-73 — reserve the SHAPE of what is arriving, rather than announcing that
                    something is. This branch blocks on two independent reads (ancestors and chain), and
                    both are reset to null on every anchor change, so until they land the whole panel
                    used to be one muted line reading "Loading…" — a fraction of the height the thought
                    will take, which then jumps several hundred pixels under the reader's thumb. On a
                    phone, combined with DISCUSSION-49's arrival scroll, that one line was what a member
                    scrolled a full screen to reach. The block below approximates one chain node (avatar
                    circle + author row + three body lines), repeated twice, so the reflow is small and
                    the column already has its geometry. `home.loading` is kept as the ACCESSIBLE name
                    on a live region — a skeleton is decorative and says nothing to a screen reader —
                    which also means this needs no new catalog key. */}
                <div
                    role="status"
                    aria-live="polite"
                    aria-label={localizer("home.loading")}
                    className="px-3 py-4"
                    data-playwright-id="thought-chain-loading"
                >
                    {[0, 1].map((row) => (
                        <div key={row} className="mb-6 flex min-w-0 gap-3">
                            <Skeleton className="size-10 shrink-0 rounded-full sm:size-12" />
                            <div className="min-w-0 flex-1 space-y-2 pt-1">
                                <Skeleton className="h-3.5 w-40 max-w-[60%]" />
                                <Skeleton className="h-3 w-full" />
                                <Skeleton className="h-3 w-full" />
                                <Skeleton className="h-3 w-3/5" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    /**
     * DISCUSSION-76 — the chain of one, which is the ONLY state a shared chain link can produce: the
     * product's own doorway is gated on a non-empty reply tree, so every arrival here comes from a
     * pasted URL, a share, or a link generated before the thread was pruned.
     *
     * <p>Until this it rendered a conversation view containing no conversation, with nothing on the
     * screen accounting for the emptiness — so the natural reading was that replies exist and failed
     * to load. That is the reading the honesty constraint forbids, and it is discharged by SAYING the
     * absence rather than by leaving it to be inferred.
     *
     * <p>Reachable only after both reads have genuinely landed: the failure branch returns above, and
     * both lists are `null` (never `[]`) while loading — the F-F2 state contract, reused rather than
     * restated. Scoped to a genuine chain of one, NOT to every leaf: a ten-node chain ending on an
     * unanswered leaf is a conversation that has caught up, and a "No replies yet" line under it is
     * noise.
     *
     * <p>It is still not a SECOND action. Since U38 (`DISCUSSION-106`, 2026-09-28) the line itself opens
     * the anchor's own reply, through the same path as the anchor's Reply control: on a phone that
     * control is a wordless disc, so the only words inviting a reply were inert. One object, one action,
     * now reachable from its words too; no new "be the first to reply" button was added beside it.
     *
     * <p><b>No key is minted.</b> `replyInvitation.zeroReplyPlaceholder` ("No one has answered this
     * yet. Yours would be the first.") is an existing sentence, translated in all 25 catalogs, and it
     * is exactly the first-run framing this row asks for: it names the absence AND invites, in one
     * line, without ever implying that replies exist and failed to load. Reuse over minting is rule
     * 11's stated preference and this doc's §11.4 precedent (`thread.continue`, `search.load_more`,
     * `thread.in_reply_to`). It carries no argument and no implicit subject beyond "this", which on a
     * chain of one is the only thing on the screen. Its other call site is the T2 reply-invitation
     * composer placeholder, which shipped to every discussion on 2026-09-12 (SV-46); there is no longer an
     * experiment arm for a second surface to disturb.
     */
    const isChainOfOne = ancestors.length === 0
        && spine.length === 1
        && (spine[0]?.replyCount ?? 0) === 0;

    return (
        <div ref={chainViewRef} className="w-full min-w-0 max-w-full" data-playwright-id="thought-chain-view">
            {backToDiscussion}

            {ancestors.length > 0 ? (
                <nav
                    className="mb-2 flex items-center gap-1 px-3 text-xs text-muted-foreground"
                    aria-label={localizer("thread.in_reply_to")}
                    data-playwright-id="thought-chain-ancestors-label"
                >
                    <CornerDownRight className="size-3.5 shrink-0" aria-hidden="true" />
                    <span>{localizer("thread.in_reply_to")}</span>
                </nav>
            ) : null}

            {/* Every ancestor is followed by the next ancestor or by the anchor, so an ancestor always
                has something below to connect to (DISCUSSION-79). */}
            {ancestors.map((node) => (
                <ChainNodeRow key={`ancestor:${node.publicId}`} node={node} common={common} isContext={true} hasNextNode={true} onReplyPublished={reloadChain} />
            ))}

            {spine.map((node, index) => {
                const isTail = index === spine.length - 1;
                const replyCount = node.replyCount ?? 0;
                const publicId = node.publicId ?? "";
                return (
                    <React.Fragment key={`spine:${publicId || index}`}>
                        <ChainNodeRow
                            node={node}
                            common={common}
                            isAnchor={index === 0}
                            isContext={false}
                            // DISCUSSION-79 — the connector means "the conversation continues below",
                            // so it is a property of there BEING a next node, not of a node existing.
                            // `isTail` was already computed here and used for the row's other tail
                            // affordances; it just never reached the line.
                            hasNextNode={!isTail}
                            suppressComposer={suppressNodeComposers}
                            replyRequest={index === 0 ? anchorReplyRequest : 0}
                            onReplyPublished={reloadChain}
                        />
                        {!isTail && replyCount > 1 ? (
                            <ChainForkSwitch
                                node={node}
                                followedChildPublicId={spine[index + 1]?.publicId ?? ""}
                                common={common}
                                onChoose={(childPublicId) => handleSwitchBranch(index, childPublicId)}
                            />
                        ) : null}
                        {isTail && replyCount > 0 ? (
                            <button
                                type="button"
                                onClick={() => handleContinueBranch(index, publicId)}
                                // DISCUSSION-66 — same coarse-pointer height floor as "Back to post"
                                // above and the two links in the thread view: a bare inline control
                                // whose hit box was its ~20 CSS px line box.
                                className="mb-3 ml-10 inline-flex items-center gap-1 text-sm font-medium text-primary no-underline hover:underline touch-manipulation [@media(pointer:coarse)]:min-h-11 sm:ml-12"
                                data-playwright-id="thought-chain-continue-branch"
                                data-thought-public-id={publicId}
                            >
                                {/* The chain view's twin of ThoughtThread's link, and the same reuse of
                                    `replies.show`. Both call sites move together or the same control
                                    reports a count on one surface and not the other. */}
                                {localizer("replies.show", replyCount)}
                                <ArrowRight className="size-4" aria-hidden="true" />
                            </button>
                        ) : null}
                    </React.Fragment>
                );
            })}

            {/* Sits exactly where the `replies.show` tail above would otherwise be — the two are
                mutually exclusive by construction (that branch needs `replyCount > 0`, this one
                `=== 0`), so the foot of the column always says one thing about what follows. */}
            {/* U38 (`DISCUSSION-106`, S7 2026-09-28): the invitation IS the action. On a phone the anchor's own
                Reply is a wordless disc, so the words that invite now open that thought's reply, the same
                open path as the disc (one action, not a second button beside it). Where the host owns the
                composer (`suppressNodeComposers`) there is no per-node reply to open, and it stays a line. */}
            {isChainOfOne ? (
                suppressNodeComposers ? (
                    <p
                        className="mb-3 ml-10 text-sm text-muted-foreground sm:ml-12"
                        data-playwright-id="thought-chain-no-replies"
                    >
                        {localizer("replyInvitation.zeroReplyPlaceholder")}
                    </p>
                ) : (
                    <button
                        type="button"
                        className="mb-3 ml-10 block rounded-md text-left text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline touch-manipulation [@media(pointer:coarse)]:min-h-11 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 sm:ml-12"
                        data-playwright-id="thought-chain-no-replies"
                        onClick={() => setAnchorReplyRequest((request) => request + 1)}
                    >
                        {localizer("replyInvitation.zeroReplyPlaceholder")}
                    </button>
                )
            ) : null}
        </div>
    );
}

// One node in the linear column: the thought body (reused verbatim from the tree/list) with a thin
// down-connector to the next node. `isContext` nodes (ancestors) are dimmed slightly to read as
// upward context rather than the focused lineage.
function ChainNodeRow(props: {
    node: ThreadNode;
    common: ReplyTreeCommonProps;
    isAnchor?: boolean;
    isContext?: boolean;
    /**
     * Whether another node follows this one in the column (`DISCUSSION-79`). The connector is drawn
     * only when it is true: the line's whole meaning is "the conversation continues below", so on a
     * terminal node it ran 654 CSS px and stopped in mid-air, telling a member the view was truncated.
     * On a chain of one that was the only shape information on the screen, and it was wrong.
     */
    hasNextNode?: boolean;
    /** See `suppressNodeComposers` on the parent: the host owns the one composer instead. */
    suppressComposer?: boolean;
    /** U38: a nonce the parent bumps to open this node's reply, exactly as its own Reply does. */
    replyRequest?: number;
    onReplyPublished: () => void;
}) {
    const { node, common, isAnchor = false, isContext = false, hasNextNode = false,
        suppressComposer = false, replyRequest = 0, onReplyPublished } = props;
    const isMobile = useIsMobile();
    const [showEditor, setShowEditor] = useState(false);
    const replyDialogRef = useRef<DialogController | null>(null);
    const publicId = node.publicId ?? "";

    useEffect(() => {
        return () => {
            try { replyDialogRef.current?.cleanup(); } catch { /* ignore */ }
            replyDialogRef.current = null;
        };
    }, []);

    const handleReplyPublished = useCallback(() => {
        setShowEditor(false);
        onReplyPublished();
    }, [onReplyPublished]);

    const openMobileReplyDialog = useCallback(() => {
        if (replyDialogRef.current) {
            replyDialogRef.current.open();
            return;
        }
        replyDialogRef.current = showThoughtTextEditorDialog({
            localizer: common.localizer,
            ariaLabelledBy: "discussion-chain-reply-editor-replying-to",
            isUserLoggedIn: MetaHandler.isUserLoggedIn(),
            loggedInUsername: common.loggedInUsername,
            discussionPublicId: common.discussionPublicId,
            parentArgumentPublicId: publicId,
            agreementAgreementTitle: common.agreementAgreementTitle,
            disagreementAgreementTitle: common.disagreementAgreementTitle,
            replyingToUsername: node.username,
            playwrightIdPrefix: "discussion-chain-reply-editor",
            onThoughtPublished: () => handleReplyPublished(),
        });
    }, [common, publicId, node.username, handleReplyPublished]);

    const handleDiscussClicked = useCallback(() => {
        if (isMobile) {
            openMobileReplyDialog();
            return;
        }
        setShowEditor((v) => !v);
    }, [isMobile, openMobileReplyDialog]);

    // U38: the parent's invitation opens this node's reply. Opens, never toggles: a second press on the
    // invitation must not close a composer the member just asked for.
    useEffect(() => {
        if (replyRequest <= 0 || isContext || suppressComposer) {
            return;
        }
        if (isMobile) {
            openMobileReplyDialog();
        } else {
            setShowEditor(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [replyRequest]);

    return (
        <div
            className={`relative ${isContext ? "opacity-80" : ""}`}
            data-playwright-id="thought-chain-node"
            data-thought-public-id={publicId}
            data-thought-chain-anchor={isAnchor ? "true" : undefined}
        >
            {/* Thin connector tying this node's avatar down to the next node in the column. Drawn only
                when there IS a next node (DISCUSSION-79). The rest colour is the full `--border` token
                with no borrowed alpha, matching the reply-tree rail's fix in
                replyThreadConnectorStyles.ts: `/70` measured 2.43:1 there, under 1.4.11's 3:1, and this
                is the same 1 px line in the same vocabulary (DISCUSSION-71). */}
            {hasNextNode ? (
                <div
                    className="pointer-events-none absolute left-7 top-14 bottom-0 w-px bg-border sm:left-8"
                    aria-hidden="true"
                />
            ) : null}
            <ThoughtDisplayerInDiscussion
                argument={node}
                conversationId={null}
                discussionSlug={common.discussionSlug}
                discussionTitle={common.discussionTitle}
                isAuthorTheLoggedInUser={node.username === common.loggedInUsername}
                localizer={common.localizer}
                isSensitive={common.isSensitive}
                suppressSpoilerGuard={common.suppressSpoilerGuards}
                onDiscussClicked={isContext || suppressComposer ? undefined : handleDiscussClicked}
                contentWrapperClassName="ml-10 sm:ml-12"
            />
            {showEditor && !isMobile && !suppressComposer ? (
                <div className="ml-10 mt-1 sm:ml-12">
                    <ThoughtTextEditor
                        localizer={common.localizer}
                        agreementAgreementTitle={common.agreementAgreementTitle}
                        disagreementAgreementTitle={common.disagreementAgreementTitle}
                        isUserLoggedIn={MetaHandler.isUserLoggedIn()}
                        loggedInUsername={common.loggedInUsername}
                        discussionPublicId={common.discussionPublicId}
                        parentArgumentPublicId={publicId}
                        autoFocus={true}
                        onThoughtPublished={handleReplyPublished}
                        playwrightIdPrefix="discussion-chain-reply-editor"
                        hideAvatarOnMobile={true}
                    />
                </div>
            ) : null}
        </div>
    );
}

// The fork affordance under a node whose lineage has more than one child. Collapsed, it names the
// followed branch; expanded, it lists the sibling replies (paginated) so the reader can switch which
// branch the chain follows below this point.
function ChainForkSwitch(props: {
    node: ThreadNode;
    followedChildPublicId: string;
    common: ReplyTreeCommonProps;
    onChoose: (childPublicId: string) => void;
}) {
    const { node, followedChildPublicId, common, onChoose } = props;
    const { localizer } = common;
    const [expanded, setExpanded] = useState(false);
    const [options, setOptions] = useState<ThreadNode[] | null>(null);
    const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
    const [hasMore, setHasMore] = useState(false);

    const parentPublicId = node.publicId ?? "";
    const otherCount = Math.max(0, (node.replyCount ?? 0) - 1);

    const loadPage = useCallback(async (cursor?: string) => {
        try {
            const resp = await ClientFactory.listThoughtReplies<ThreadNode[]>(parentPublicId, cursor, FORK_PICKER_PAGE_SIZE);
            const page = toNodeArray(resp.data);
            setOptions((prev) => (cursor && prev ? [...prev, ...page] : page));
            setHasMore(page.length === FORK_PICKER_PAGE_SIZE);
            const last = page[page.length - 1];
            setNextCursor(last?.paginationParameter ?? undefined);
        } catch (err) {
            logger.error("[ChainForkSwitch] failed to load siblings", err);
            setOptions((prev) => prev ?? []);
            setHasMore(false);
        }
    }, [parentPublicId]);

    const handleToggle = useCallback(() => {
        setExpanded((prev) => {
            const next = !prev;
            if (next && options === null) {
                void loadPage(undefined);
            }
            return next;
        });
    }, [options, loadPage]);

    return (
        <div className="mb-3 ml-10 sm:ml-12" data-playwright-id="thought-chain-fork" data-thought-public-id={parentPublicId}>
            <button
                type="button"
                onClick={handleToggle}
                aria-expanded={expanded}
                className="inline-flex items-center gap-1.5 rounded-full border border-border/70 px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-foreground/60 hover:text-foreground [@media(pointer:coarse)]:min-h-11"
                data-playwright-id="thought-chain-fork-switch"
                data-thought-public-id={parentPublicId}
            >
                <GitBranch className="size-3.5" aria-hidden="true" />
                <span>{localizer("thread.chain.following_top_reply")}</span>
                <span aria-hidden="true">·</span>
                <span>{localizer("thread.chain.other_replies", otherCount)}</span>
                <ChevronDown className={`size-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>

            {expanded ? (
                <ul className="mt-2 flex flex-col gap-1" data-playwright-id="thought-chain-fork-options">
                    {(options ?? []).map((option) => {
                        const optionPublicId = option.publicId ?? "";
                        const isCurrent = optionPublicId === followedChildPublicId;
                        return (
                            <li key={optionPublicId}>
                                <button
                                    type="button"
                                    disabled={isCurrent}
                                    onClick={() => { if (!isCurrent) onChoose(optionPublicId); }}
                                    className={`flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-left text-xs [@media(pointer:coarse)]:min-h-11 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 ${isCurrent ? "cursor-default bg-primary/5 text-foreground" : "text-muted-foreground standard-hover hover:text-foreground"}`}
                                    data-playwright-id="thought-chain-branch-option"
                                    data-thought-public-id={optionPublicId}
                                    data-current={isCurrent ? "true" : undefined}
                                >
                                    <ProfileImage localizer={localizer}
                                        imageUrl={option.profileImageUrl}
                                        username={option.username}
                                        mode={"user"}
                                        interactive={false}
                                        className={"size-5 shrink-0"}
                                    />
                                    <span className="truncate font-medium" translate={hasDisplayUsername(option.username) ? "no" : undefined}>{getDisplayUsername(option.username, localizer)}</span>
                                    {isCurrent ? (
                                        <span className="ml-auto shrink-0 text-[0.65rem] uppercase tracking-wide text-primary">
                                            {localizer("thread.chain.current_branch")}
                                        </span>
                                    ) : null}
                                </button>
                            </li>
                        );
                    })}
                    {hasMore ? (
                        <li>
                            <button
                                type="button"
                                onClick={() => loadPage(nextCursor)}
                                className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline [@media(pointer:coarse)]:min-h-11"
                                data-playwright-id="thought-chain-fork-more"
                            >
                                {localizer("search.load_more")}
                            </button>
                        </li>
                    ) : null}
                </ul>
            ) : null}
        </div>
    );
}
